// frontend/src/utils/etaCalculations.js
// Shared utility for calculating ETA times based on route waypoints and durations

/**
 * Calculate estimated arrival times for waypoints based on departure time
 * Provides two estimates when available:
 * 1. Baseline ETA: From route segment times (OpenRouteService) - driving time without traffic
 * 2. Traffic ETA: From reference data (GPS logs) - actual driving time with normal traffic
 *
 * Supports reorderable waypoints via effectiveOrder parameter.
 * See docs/ETA_TRIP_TRACKING_REORDERABLE_WAYPOINTS.md for details.
 *
 * @param {Array} waypoints - Array of waypoints with stay_duration_minutes
 * @param {string} departureTime - Departure time in HH:mm format
 * @param {string} tripDate - Trip date in YYYY-MM-DD format
 * @param {number} pickupStopId - ID of the pickup waypoint
 * @param {number} dropoffStopId - ID of the dropoff waypoint
 * @param {Object} referenceData - Optional legacy reference-route data keyed by waypoint_id
 * @param {Array} segmentData - Optional segment metrics from route_segments
 *                             Each segment may include:
 *                             { fromWaypointId, toWaypointId, durationMinutes, orsDurationMinutes,
 *                               referenceDurationMinutes, distanceKm, isSequential }
 * @param {Array} effectiveOrder - Optional effective waypoint order for reorderable trips
 *                                Each item: { waypointId, effectiveSequence, isSkipped }
 * @returns {Object} Object containing calculated times and durations for both baseline and traffic
 */
export const calculateBookingETA = (waypoints, departureTime, tripDate, pickupStopId, dropoffStopId, referenceData = null, segmentData = null, effectiveOrder = null) => {
  if (!waypoints || waypoints.length === 0 || !departureTime || !tripDate) {
    return null;
  }

  // Sort waypoints by effective order if available, otherwise by sequence number
  // When effectiveOrder is provided, it includes custom reordering and skip logic
  let sortedWaypoints;
  if (effectiveOrder && effectiveOrder.length > 0) {
    // Build a map of waypointId -> effectiveSequence
    const orderMap = new Map(
      effectiveOrder.map(o => [o.waypointId || o.waypoint_id, o.effectiveSequence || o.effective_sequence])
    );
    // Build a set of skipped waypoint IDs
    const skippedIds = new Set(
      effectiveOrder
        .filter(o => o.isSkipped || o.is_skipped)
        .map(o => o.waypointId || o.waypoint_id)
    );

    // Filter out skipped waypoints and sort by effective sequence
    sortedWaypoints = [...waypoints]
      .filter(wp => !skippedIds.has(wp.id))
      .sort((a, b) => (orderMap.get(a.id) || 0) - (orderMap.get(b.id) || 0));
  } else {
    // Default: sort by sequence number
    sortedWaypoints = [...waypoints].sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
  }

  // Create base datetime from trip date and departure time
  const baseDateTimeStr = `${tripDate}T${departureTime}`;
  const baseDateTime = new Date(baseDateTimeStr);

  if (isNaN(baseDateTime.getTime())) {
    console.warn('[calculateBookingETA] Invalid base datetime:', baseDateTimeStr);
    return null;
  }

  // When waypoints carry explicit is_skipped flags from the backend, trust them.
  // The backend determines skip status by checking whether a shortcut/spanning variant
  // actually exists to bypass the waypoint. A waypoint with is_required=0 but
  // is_skipped=false means the shuttle still physically drives through it (no shortcut
  // selected), so it must remain in the ETA path.
  // Without these flags we fall back to is_required-based heuristics (see below).
  const hasExplicitSkipFlags = waypoints.some(wp => wp.is_skipped !== undefined || wp.isSkipped !== undefined);

  // Find pickup and dropoff waypoints
  const pickupWaypoint = sortedWaypoints.find(wp => wp.id === pickupStopId);
  const dropoffWaypoint = sortedWaypoints.find(wp => wp.id === dropoffStopId);

  if (!pickupWaypoint || !dropoffWaypoint) {
    console.warn('[calculateBookingETA] Pickup or dropoff waypoint not found');
    return null;
  }

  // Find the start location (first waypoint)
  const startWaypoint = sortedWaypoints[0];
  const isPickupStart = startWaypoint.id === pickupStopId;

  // Build segment lookup map for quick access: "fromId-toId" -> segment
  // This includes both sequential segments and shortcut segments
  const segmentMap = {};
  if (segmentData && Array.isArray(segmentData)) {
    for (const segment of segmentData) {
      const key = `${segment.fromWaypointId}-${segment.toWaypointId}`;
      segmentMap[key] = segment;
    }
  }

  const getSegment = (fromWaypointId, toWaypointId) => {
    const key = `${fromWaypointId}-${toWaypointId}`;
    return segmentMap[key] || null;
  };

  const getOrsSegmentDuration = (fromWaypointId, toWaypointId) => {
    const segment = getSegment(fromWaypointId, toWaypointId);
    if (!segment) return null;

    const orsDuration = Number(segment.orsDurationMinutes);
    if (Number.isFinite(orsDuration) && orsDuration > 0) {
      return orsDuration;
    }
    if (Object.prototype.hasOwnProperty.call(segment, 'orsDurationMinutes')) return null;
    const legacyDuration = Number(segment.durationMinutes);
    return Number.isFinite(legacyDuration) && legacyDuration > 0 ? legacyDuration : null;
  };

  const getReferenceSegmentDuration = (fromWaypointId, toWaypointId) => {
    const segment = getSegment(fromWaypointId, toWaypointId);
    if (!segment) return null;

    const referenceDuration = Number(segment.referenceDurationMinutes);
    return Number.isFinite(referenceDuration) && referenceDuration > 0 ? referenceDuration : null;
  };

  const hasDirectSegment = (fromWaypointId, toWaypointId) => {
    const key = `${fromWaypointId}-${toWaypointId}`;
    return Boolean(segmentMap[key]);
  };

  // Determine which waypoints will actually be visited by the shuttle
  // Prefer explicit skip state from the trip plan when present. Optional waypoints
  // can still be visited if no shortcut/variant actually bypasses them.
  const candidateWaypoints = sortedWaypoints.filter(wp => {
    // Explicit backend flags override is_required heuristics.
    // e.g. Bayview 401 (is_required=0, is_skipped=false) stays in the path
    // because no shortcut variant bypasses it — the shuttle drives through it.
    if (hasExplicitSkipFlags) {
      return !(wp.is_skipped || wp.isSkipped);
    }

    // Fallback when the caller only has static route waypoint metadata.
    // A waypoint is visited if:
    // 1. It's required (is_required = true)
    // 2. It's the user's pickup location
    // 3. It's the user's dropoff location
    // 4. It's the start waypoint (first in sequence)
    // Always visit the start waypoint
    if (wp.sequence_number === sortedWaypoints[0].sequence_number) return true;
    // Always visit required waypoints
    if (wp.is_required === true || wp.is_required === 1) return true;
    // Always visit pickup and dropoff
    if (wp.id === pickupStopId || wp.id === dropoffStopId) return true;
    // Skip other waypoints (they're skippable and not used by this booking)
    return false;
  }).filter(wp => {
    // Only include waypoints up to and including dropoff
    return wp.sequence_number <= dropoffWaypoint.sequence_number;
  });

  let visitedWaypoints = candidateWaypoints;
  if (!hasExplicitSkipFlags && Array.isArray(segmentData) && candidateWaypoints.length > 1) {
    const waypointIndexById = new Map(sortedWaypoints.map((wp, index) => [wp.id, index]));
    const connectedPath = [candidateWaypoints[0]];

    for (let i = 1; i < candidateWaypoints.length; i++) {
      const previousWaypoint = connectedPath[connectedPath.length - 1];
      const nextWaypoint = candidateWaypoints[i];

      if (hasDirectSegment(previousWaypoint.id, nextWaypoint.id)) {
        connectedPath.push(nextWaypoint);
        continue;
      }

      const previousIndex = waypointIndexById.get(previousWaypoint.id);
      const nextIndex = waypointIndexById.get(nextWaypoint.id);

      if (Number.isInteger(previousIndex) && Number.isInteger(nextIndex) && nextIndex > previousIndex) {
        for (let j = previousIndex + 1; j <= nextIndex; j++) {
          const intermediateWaypoint = sortedWaypoints[j];
          if (!connectedPath.some((wp) => wp.id === intermediateWaypoint.id)) {
            connectedPath.push(intermediateWaypoint);
          }
        }
      } else {
        connectedPath.push(nextWaypoint);
      }
    }

    visitedWaypoints = connectedPath;
  }

  // Calculate cumulative times for ORS baseline
  let baselineCumulativeMinutes = 0;
  const baselineWaypointTimes = {};
  let baselineChainIntact = true;

  // Calculate cumulative times for reference-route duration
  let referenceCumulativeMinutes = 0;
  const referenceWaypointTimes = {};
  let referenceChainIntact = true;
  let travelCumulativeMinutes = 0;
  let travelChainIntact = true;
  const travelWaypointTimes = {};
  const hasLegacyReferenceData = referenceData && Object.keys(referenceData).length > 0;

  // Track visited waypoint indices for building the actual path
  for (let i = 0; i < visitedWaypoints.length; i++) {
    const waypoint = visitedWaypoints[i];
    const prevVisitedWaypoint = i > 0 ? visitedWaypoints[i - 1] : null;

    // Get stay duration at this waypoint
    const stayDuration = parseFloat(waypoint.stay_duration_minutes) || 0;

    // The effective duration is already resolved per constituent on the
    // server. A composed mixed span can be complete even though neither pure
    // source aggregate is available; do not reconstruct it from those totals.
    if (prevVisitedWaypoint) {
      const segment = getSegment(prevVisitedWaypoint.id, waypoint.id);
      const duration = Number(segment?.durationMinutes);
      if (segment?.durationMinutes != null && Number.isFinite(duration) && duration > 0) {
        travelCumulativeMinutes += duration;
      } else {
        travelChainIntact = false;
      }
    }
    travelWaypointTimes[waypoint.id] = {
      arrivalTime: travelChainIntact ? new Date(baseDateTime.getTime() + travelCumulativeMinutes * 60000) : null,
      cumulativeMinutes: travelChainIntact ? travelCumulativeMinutes : null
    };

    // ORS baseline calculation using segment data with shortcuts
    if (prevVisitedWaypoint) {
      const segmentDuration = getOrsSegmentDuration(prevVisitedWaypoint.id, waypoint.id);
      if (segmentDuration !== null) {
        baselineCumulativeMinutes += segmentDuration;
      } else {
        baselineChainIntact = false;
      }
    }

    baselineWaypointTimes[waypoint.id] = {
      arrivalTime: baselineChainIntact
        ? new Date(baseDateTime.getTime() + baselineCumulativeMinutes * 60000)
        : null,
      cumulativeMinutes: baselineChainIntact ? baselineCumulativeMinutes : null
    };

    // Reference-route calculation from split route_segments columns.
    // Legacy waypoint-keyed referenceData remains a fallback for untouched callers.
    if (prevVisitedWaypoint) {
      const segmentReferenceDuration = getReferenceSegmentDuration(prevVisitedWaypoint.id, waypoint.id);

      if (segmentReferenceDuration !== null) {
        referenceCumulativeMinutes += segmentReferenceDuration;
      } else if (hasLegacyReferenceData && referenceData[waypoint.id]) {
        const tripDuration = parseFloat(referenceData[waypoint.id].trip_duration_minutes);
        if (Number.isFinite(tripDuration) && tripDuration > 0) {
          referenceCumulativeMinutes += tripDuration;
        } else {
          referenceChainIntact = false;
        }
      } else {
        referenceChainIntact = false;
      }
    }

    referenceWaypointTimes[waypoint.id] = {
      arrivalTime: referenceChainIntact
        ? new Date(baseDateTime.getTime() + referenceCumulativeMinutes * 60000)
        : null,
      cumulativeMinutes: referenceChainIntact ? referenceCumulativeMinutes : null
    };

    // Add stay duration if this waypoint has pickups/dropoffs and is actually being visited
    // (affects cumulative time for subsequent waypoints)
    if (stayDuration > 0 && (waypoint.can_pickup || waypoint.can_dropoff)) {
      travelCumulativeMinutes += stayDuration;
      baselineCumulativeMinutes += stayDuration;
      if (referenceChainIntact) {
        referenceCumulativeMinutes += stayDuration;
      }
    }
  }

  const hasSegmentData = segmentData && segmentData.length > 0;
  const baselineAvailable = hasSegmentData && baselineChainIntact;
  const referenceAvailable = referenceChainIntact && referenceWaypointTimes[dropoffStopId]?.cumulativeMinutes != null;

  // Calculate baseline durations
  const baselineStartToPickupMinutes = isPickupStart
    ? 0
    : (baselineWaypointTimes[pickupStopId]?.cumulativeMinutes ?? null);
  const baselinePickupToDropoffMinutes =
    baselineWaypointTimes[dropoffStopId]?.cumulativeMinutes != null &&
    baselineWaypointTimes[pickupStopId]?.cumulativeMinutes != null
      ? baselineWaypointTimes[dropoffStopId].cumulativeMinutes - baselineWaypointTimes[pickupStopId].cumulativeMinutes
      : null;
  const baselineTotalTripMinutes = baselineWaypointTimes[dropoffStopId]?.cumulativeMinutes ?? null;

  // Calculate reference durations
  const referenceStartToPickupMinutes = isPickupStart
    ? 0
    : (referenceWaypointTimes[pickupStopId]?.cumulativeMinutes ?? null);
  const referencePickupToDropoffMinutes =
    referenceWaypointTimes[dropoffStopId]?.cumulativeMinutes != null &&
    referenceWaypointTimes[pickupStopId]?.cumulativeMinutes != null
      ? referenceWaypointTimes[dropoffStopId].cumulativeMinutes - referenceWaypointTimes[pickupStopId].cumulativeMinutes
      : null;
  const referenceTotalTripMinutes = referenceWaypointTimes[dropoffStopId]?.cumulativeMinutes ?? null;
  const travelStartToPickupMinutes = isPickupStart ? 0 : travelWaypointTimes[pickupStopId]?.cumulativeMinutes ?? null;
  const travelTotalTripMinutes = travelWaypointTimes[dropoffStopId]?.cumulativeMinutes ?? null;
  const travelPickupToDropoffMinutes = travelTotalTripMinutes != null && travelStartToPickupMinutes != null
    ? travelTotalTripMinutes - travelStartToPickupMinutes : null;

  const referenceBlock = {
    pickupLocation: {
      name: pickupWaypoint.name,
      address: pickupWaypoint.address,
      time: referenceWaypointTimes[pickupStopId]?.arrivalTime ?? null,
      minutesFromStart: referenceStartToPickupMinutes,
      isStartLocation: isPickupStart
    },
    dropoffLocation: {
      name: dropoffWaypoint.name,
      address: dropoffWaypoint.address,
      time: referenceWaypointTimes[dropoffStopId]?.arrivalTime ?? null,
      minutesFromPickup: referencePickupToDropoffMinutes,
      minutesFromStart: referenceTotalTripMinutes
    },
    summary: {
      startToPickupMinutes: referenceStartToPickupMinutes,
      pickupToDropoffMinutes: referencePickupToDropoffMinutes,
      totalTripMinutes: referenceTotalTripMinutes,
      isPickupStart,
      dataSource: 'reference_segments'
    },
    available: referenceAvailable
  };

  return {
    available: travelTotalTripMinutes != null,
    departureTime: baseDateTime,
    startLocation: {
      name: startWaypoint.name,
      address: startWaypoint.address,
      time: baseDateTime
    },
    // Baseline ETA (segment data - no traffic)
    baseline: {
      pickupLocation: {
        name: pickupWaypoint.name,
        address: pickupWaypoint.address,
        time: baselineWaypointTimes[pickupStopId]?.arrivalTime,
        minutesFromStart: baselineStartToPickupMinutes,
        isStartLocation: isPickupStart
      },
      dropoffLocation: {
        name: dropoffWaypoint.name,
        address: dropoffWaypoint.address,
        time: baselineWaypointTimes[dropoffStopId]?.arrivalTime,
        minutesFromPickup: baselinePickupToDropoffMinutes,
        minutesFromStart: baselineTotalTripMinutes
      },
      summary: {
        startToPickupMinutes: baselineStartToPickupMinutes,
        pickupToDropoffMinutes: baselinePickupToDropoffMinutes,
        totalTripMinutes: baselineTotalTripMinutes,
        isPickupStart,
        dataSource: 'ors_segments'
      },
      available: baselineAvailable
    },
    reference: referenceBlock,
    // Legacy alias for untouched callers
    traffic: referenceBlock,
    // Primary booking estimate uses the server-owned effective travel chain.
    pickupLocation: {
      name: pickupWaypoint.name,
      address: pickupWaypoint.address,
      time: travelWaypointTimes[pickupStopId]?.arrivalTime ?? null,
      minutesFromStart: travelStartToPickupMinutes,
      isStartLocation: isPickupStart
    },
    dropoffLocation: {
      name: dropoffWaypoint.name,
      address: dropoffWaypoint.address,
      time: travelWaypointTimes[dropoffStopId]?.arrivalTime ?? null,
      minutesFromPickup: travelPickupToDropoffMinutes,
      minutesFromStart: travelTotalTripMinutes
    },
    summary: {
      startToPickupMinutes: travelStartToPickupMinutes,
      pickupToDropoffMinutes: travelPickupToDropoffMinutes,
      totalTripMinutes: travelTotalTripMinutes,
      isPickupStart,
      dataSource: travelTotalTripMinutes != null ? 'route_segments' : 'no_data'
    }
  };
};

/**
 * Extract duration data from route_reference_locations
 * Calculates durations based on actual GPS timestamps
 * 
 * @param {Array} referenceLocations - Array of GPS log points with timestamps
 * @returns {Object} Map of waypoint_id to duration data
 */
export const extractReferenceDataDurations = (referenceLocations) => {
  if (!referenceLocations || referenceLocations.length === 0) {
    return null;
  }
  
  const durations = {};
  const sortedLocations = [...referenceLocations].sort((a, b) => 
    new Date(a.timestamp) - new Date(b.timestamp)
  );
  
  for (let i = 0; i < sortedLocations.length; i++) {
    const current = sortedLocations[i];
    const next = sortedLocations[i + 1];
    
    if (current.waypoint_id) {
      // Calculate trip duration to reach this waypoint
      let tripDurationMinutes = 0;
      if (i > 0) {
        const prevWaypoint = sortedLocations.slice(0, i).reverse().find(loc => loc.waypoint_id);
        if (prevWaypoint) {
          const timeDiff = new Date(current.timestamp) - new Date(prevWaypoint.timestamp);
          tripDurationMinutes = timeDiff / (1000 * 60); // Convert to minutes
        }
      }
      
      // Calculate stay duration at this waypoint
      let stayDurationMinutes = 0;
      if (next && !next.waypoint_id) {
        // If next point is not a waypoint, we're still at this stop
        const departurePoint = sortedLocations.slice(i + 1).find(loc => 
          loc.waypoint_id || loc.is_moving
        );
        if (departurePoint) {
          const stayTime = new Date(departurePoint.timestamp) - new Date(current.timestamp);
          stayDurationMinutes = stayTime / (1000 * 60);
        }
      }
      
      durations[current.waypoint_id] = {
        trip_duration_minutes: Math.round(tripDurationMinutes * 10) / 10, // Round to 1 decimal
        stay_duration_minutes: Math.round(stayDurationMinutes * 10) / 10
      };
    }
  }
  
  return durations;
};

/**
 * Format duration in minutes to human-readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
/**
 * Format duration text with proper i18n support
 * @param {number} minutes - Duration in minutes
 * @param {Function} t - i18n translation function (optional for backwards compatibility)
 * @returns {string} Formatted duration text
 */
export const formatDurationText = (minutes, t = null) => {
  if (!minutes || minutes === 0) return '';

  // Use floor instead of round to match displayed times
  // (e.g., 8:35 + 13.5min = 8:48:30 displayed as "8:48", so should show "13 min" not "14 min")
  const totalMinutes = Math.floor(minutes);

  // Default English fallbacks if no translation function provided
  const minText = t ? t('min') : 'min';
  const hrText = t ? t('hr') : 'hr';

  if (totalMinutes < 60) {
    return `${totalMinutes} ${minText}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} ${hrText}`;
  }

  return `${hours} ${hrText} ${remainingMinutes} ${minText}`;
};
